import { PaymentMethodDetails } from '@zooplus/checkout-state';

import { CardType, Nullable } from '@types-internal';

export const supportedPaymentMethods = {
  BILL: 'R',
  DIRECT_DEBIT_SEPA: 'BS',
  IDEAL: 'ID',
  APPLE_PAY: 'AP',
  GOOGLE_PAY: 'GP',
  BANCONTACT: 'BC',
  BANCONTACT_MOBIL: 'BM',
  CASH_ON_DELIVERY: 'CD',
  CREDIT_CARD: 'K',
  DOTPAY: 'DP',
  ONLINE_BANKING_PL: 'OP',
  RIVERTY_INVOICE: 'RI',
  MULTIBANCO: 'MB',
  PAYPAL: 'PP',
  PAYTRAIL: 'PT',
  PREPAYMENT: 'VK',
  TRUSTLY: 'TY',
  TWINT: 'TW',
  KLARNA_PAY_LATER: 'KL',
  KLARNA_PAY_OVER_TIME: 'KO',
} as const;

export const flippedSupportedPMs = {
  R: 'BILL',
  BS: 'DIRECT_DEBIT_SEPA',
  ID: 'IDEAL',
  AP: 'APPLE_PAY',
  GP: 'GOOGLE_PAY',
  BC: 'BANCONTACT',
  BM: 'BANCONTACT_MOBILE',
  CD: 'CASH_ON_DELIVERY',
  K: 'CREDIT_CARD',
  DP: 'DOTPAY',
  OP: 'ONLINE_BANKING_PL',
  MB: 'MULTIBANCO',
  PP: 'PAYPAL',
  PT: 'PAYTRAIL',
  VK: 'PREPAYMENT',
  TY: 'TRUSTLY',
  RI: 'RIVERTY_INVOICE',
  TW: 'TWINT',
  KL: 'KLARNA_PAY_LATER',
  KO: 'KLARNA_PAY_OVER_TIME',
} as const;

export const bancontactPaymentMethods = ['BC', 'BM'];

export type PaymentMethod = keyof typeof supportedPaymentMethods;
export type PaymentMethodShortName = (typeof supportedPaymentMethods)[PaymentMethod];
export type CreditCardWithPaymentMethod = CreditCard & {
  paymentMethod: string | null;
};
export type CreditCardBrand =
  | 'MASTER_CARD'
  | 'VISA'
  | 'DINERS_CLUB'
  | 'AMERICAN_EXPRESS'
  | 'MAESTRO'
  | 'CARTE_BANCAIRE'
  | 'OTHER';

export type CreditCard = {
  brand: CreditCardBrand | null;
  pspIdentifier: string | null;
  pspIdentifierAlternative: string | null;
  psp: string | null;
  createdAt: number | null;
  maskedNumber: string | null;
  bin: string | null;
  holderName: string | null;
  expirationMonth: string | null;
  expirationYear: string | null;
  expired: boolean;
  favorite: boolean;
  nonce: string | null;
};

export const cardTypeOfCreditCardBrand: Record<CreditCardBrand, Nullable<CardType>> = {
  MASTER_CARD: 'mastercard',
  VISA: 'visa',
  DINERS_CLUB: 'diners-club',
  AMERICAN_EXPRESS: 'american-express',
  MAESTRO: 'maestro',
  CARTE_BANCAIRE: 'cartebancaire',
  OTHER: null,
};

export type CardTypeOfCreditCardBrand = typeof cardTypeOfCreditCardBrand;

export const creditCardBrandOfCardType: Record<CardType, CreditCardBrand> = {
  mastercard: 'MASTER_CARD',
  visa: 'VISA',
  'diners-club': 'DINERS_CLUB',
  'american-express': 'AMERICAN_EXPRESS',
  maestro: 'MAESTRO',
  cartebancaire: 'CARTE_BANCAIRE',
  discover: 'OTHER',
  jcb: 'OTHER',
  unionpay: 'OTHER',
  elo: 'OTHER',
  mir: 'OTHER',
  hiper: 'OTHER',
  hipercard: 'OTHER',
  bcmc: 'OTHER',
};

export type PaymentMethodCheckout = {
  paymentMethod: string | null;
  paymentMethodShortName: string | null;
  availableForGuest: boolean;
  order: number | null;
  preferredPayment: boolean;
  details?: PaymentMethodDetails;
  autoshipmentEligible: boolean;
  disabled: boolean;
  default: boolean;
};
